import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { Badge } from "@/components/ui/badge";
import { OrganizerForm } from "@/components/Organizers/OrganizerForm";
import { useOrganizers } from "@/hooks/useOrganizers";
import { useTeams } from "@/hooks/useTeams";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import { useCreateEvent, useUpdateEvent } from "@/hooks/useEvents";
import { useToast } from "@/hooks/useToast";
import { EVENT_TYPES } from "@/utils/constants";
import type { Tables } from "@/types/database";

const eventSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  event_date: z.string().min(1, "La date est requise"),
  event_time: z.string().optional(),
  location: z.string().min(1, "Le lieu est requis"),
  event_type: z.array(z.string()).min(1, "Au moins un type est requis"),
  event_format: z.enum(["Prestation", "Co-organization"]),
  organizer_id: z.string().min(1, "L'organisateur est requis"),
  team_id: z.string().optional(),
  expected_attendees: z.coerce.number().optional(),
  budget_estimate: z.coerce.number().optional(),
  internal_notes: z.string().optional(),
});

type EventFormData = z.infer<typeof eventSchema>;

interface EventFormProps {
  open: boolean;
  onClose: () => void;
  event?: Tables<"events">;
}

export function EventForm({ open, onClose, event }: EventFormProps) {
  const [orgFormOpen, setOrgFormOpen] = useState(false);
  const { data: organizers = [] } = useOrganizers();
  const { data: teams = [] } = useTeams();
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: event
      ? {
          name: event.name,
          event_date: event.event_date,
          event_time: event.event_time || "",
          location: event.location,
          event_type: event.event_type,
          event_format: event.event_format,
          organizer_id: event.organizer_id,
          team_id: event.team_id || "",
          expected_attendees: event.expected_attendees || undefined,
          budget_estimate: event.budget_estimate || undefined,
          internal_notes: event.internal_notes || "",
        }
      : {
          event_format: "Prestation",
          event_type: [],
        },
  });

  const selectedTypes = watch("event_type") || [];
  const selectedTeamId = watch("team_id");
  const { data: teamMembers = [] } = useTeamMembers(selectedTeamId);

  const onSubmit = async (data: EventFormData) => {
    try {
      if (event) {
        await updateEvent.mutateAsync({
          id: event.id,
          updates: {
            ...data,
            team_id: data.team_id || null,
          },
        });
        toast({ title: "Événement mis à jour" });
      } else {
        await createEvent.mutateAsync({
          ...data,
          team_id: data.team_id || null,
        });
        toast({ title: "Événement créé" });
      }
      reset();
      onClose();
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        variant: "destructive",
      });
    }
  };

  const toggleEventType = (type: string) => {
    const current = selectedTypes;
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    setValue("event_type", updated);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-2xl overflow-y-auto sm:w-full">
        <DialogHeader>
          <DialogTitle>
            {event ? "Modifier l'événement" : "Nouvel événement"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="name">Nom de l&apos;événement *</Label>
              <Input id="name" {...register("name")} />
              {errors.name && (
                <p className="text-sm text-destructive">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="event_date">Date *</Label>
              <Input id="event_date" type="date" {...register("event_date")} />
              {errors.event_date && (
                <p className="text-sm text-destructive">
                  {errors.event_date.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="event_time">Heure</Label>
              <Input id="event_time" type="time" {...register("event_time")} />
            </div>

            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="location">Lieu *</Label>
              <Input id="location" {...register("location")} />
              {errors.location && (
                <p className="text-sm text-destructive">
                  {errors.location.message}
                </p>
              )}
            </div>

            <div className="sm:col-span-2 space-y-2">
              <div className="flex items-center gap-1">
                <Label>Type(s) d&apos;événement *</Label>
                <HelpTooltip content="Sélectionnez un ou plusieurs types qui décrivent le mieux votre événement. Cela aide à organiser et filtrer les événements." />
              </div>
              <div className="flex flex-wrap gap-2">
                {EVENT_TYPES.map((type) => (
                  <Button
                    key={type}
                    type="button"
                    variant={
                      selectedTypes.includes(type) ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => toggleEventType(type)}
                  >
                    {type}
                  </Button>
                ))}
              </div>
              {errors.event_type && (
                <p className="text-sm text-destructive">
                  {errors.event_type.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label>Format *</Label>
                <HelpTooltip content="Prestation : CBM fournit un service complet (artistes, son, logistique). Co-organisation : partenariat où les responsabilités sont partagées avec l'organisateur." />
              </div>
              <Select
                value={watch("event_format")}
                onValueChange={(v) =>
                  setValue(
                    "event_format",
                    v as "Prestation" | "Co-organization",
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Prestation">Prestation</SelectItem>
                  <SelectItem value="Co-organization">
                    Co-organisation
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Organisateur *</Label>
              <div className="flex gap-2">
                <Select
                  value={watch("organizer_id")}
                  onValueChange={(v) => setValue("organizer_id", v)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {organizers.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setOrgFormOpen(true)}
                  title="Ajouter un organisateur"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {errors.organizer_id && (
                <p className="text-sm text-destructive">
                  {errors.organizer_id.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label>Équipe</Label>
                <HelpTooltip content="Équipe musicale qui sera assignée à cet événement. Laissez vide si non déterminé." />
              </div>
              <Select
                value={watch("team_id") || ""}
                onValueChange={(v) => setValue("team_id", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTeamId && teamMembers.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Membres de l'équipe
                </Label>
                <div className="rounded-lg border p-3 space-y-2">
                  {teamMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="font-medium">
                        {member.user.full_name || member.user.email}
                      </span>
                      <div className="flex gap-2">
                        {member.role_in_team && (
                          <Badge variant="secondary" className="text-xs">
                            {member.role_in_team}
                          </Badge>
                        )}
                        {member.instrument && (
                          <Badge variant="outline" className="text-xs">
                            {member.instrument}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="expected_attendees">Jauge estimée</Label>
                <HelpTooltip content="Nombre de participants attendus. Utile pour planifier la logistique, le matériel et l'équipe nécessaire." />
              </div>
              <Input
                id="expected_attendees"
                type="number"
                {...register("expected_attendees")}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="budget_estimate">Budget estimé (€)</Label>
                <HelpTooltip content="Budget prévisionnel avant établissement du devis détaillé. Permet d'évaluer rapidement la faisabilité." />
              </div>
              <Input
                id="budget_estimate"
                type="number"
                step="0.01"
                {...register("budget_estimate")}
              />
            </div>

            <div className="sm:col-span-2 space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="internal_notes">Notes internes</Label>
                <HelpTooltip content="Notes visibles uniquement par l'équipe CBM. Utilisez ce champ pour des remarques internes, rappels ou informations sensibles." />
              </div>
              <Textarea id="internal_notes" {...register("internal_notes")} />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Enregistrement..."
                : event
                  ? "Mettre à jour"
                  : "Créer"}
            </Button>
          </div>
        </form>

        <OrganizerForm
          open={orgFormOpen}
          onClose={() => setOrgFormOpen(false)}
          onSuccess={(newOrgId) => setValue("organizer_id", newOrgId)}
        />
      </DialogContent>
    </Dialog>
  );
}
